import {getActiveSession} from 'common/HelperFunctions'
import {FC, ReactNode, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {
  checkGenerateVoucherChunk,
  getGenerateVoucherRun,
  runGenerateVoucherChunk,
} from 'setup/redux/Actions/GenerateVoucher/generateVoucher'

import RunNotice from './RunNotice'
import {InFlight, RunDriverContext} from './runDriverContext'
import {PartStatus, Run, RunPart, runProgress} from './types'

// After one of these the run waits for a person: reading decides the first, the others need a new run.
const STOPS: PartStatus[] = ['unknown', 'gate', 'refused']

type Reply = {ok: true; chunk: RunPart; run: Run} | {ok: false; message: string}

// Every request goes to the school session the browser's storage names, which all the portal's tabs share.
const activeSession = () => getActiveSession()?.db_postfix ?? null

/**
 * Sends a run's parts one after another, as PHP's progress page does, while the user works on
 * other portal pages; closing or reloading the tab stops it after the part in flight. Shows the
 * run's progress in the tab's title, and in a notice on the other pages.
 */
const RunDriverProvider: FC<{children: ReactNode}> = ({children}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [run, setRun] = useState<Run | null>(null)
  const [driving, setDriving] = useState(false)
  const [inFlight, setInFlight] = useState<InFlight | null>(null)
  const [queued, setQueued] = useState<Set<number> | null>(null)
  const [seen, setSeen] = useState<string | null>(null)
  const runRef = useRef<Run | null>(null)
  const drivingRef = useRef(false)
  const pauseRef = useRef(false)
  const alive = useRef(true)
  const baseTitle = useRef<string | null>(null)

  const text = (id: string) => intl.formatMessage({id: `FEE.GENERATE_VOUCHERS.${id}`})

  const keep = (next: Run | null) => {
    runRef.current = next
    setRun(next)
  }

  const setBusy = (busy: boolean) => {
    drivingRef.current = busy
    setDriving(busy)
    // A new pass makes what the user saw of the run before out of date.
    if (busy) setSeen(null)
  }

  const ask = (action: (onSuccess: any, onFailure: any) => any) =>
    new Promise<Reply>((resolve) => {
      dispatch(
        action(
          (res: any) => resolve({ok: true, ...res.result}),
          (res: any) =>
            resolve({ok: false, message: res?.message || intl.formatMessage({id: 'GENERAL.ERROR'})})
        )
      )
    })

  /** The run as node holds it now, or null where it cannot be read. */
  const reread = (id: number) =>
    new Promise<Run | null>((resolve) => {
      dispatch(
        getGenerateVoucherRun(
          id,
          (res: any) => resolve(res.result),
          () => resolve(null)
        )
      )
    })

  /** Sends parts until none is left, one stops the run, or Pause. */
  const drive = async (start: Run, resend: PartStatus[] = []) => {
    if (drivingRef.current) return
    keep(start)
    setBusy(true)
    pauseRef.current = false
    const session = activeSession()
    // Each part is sent at most once per pass, so one that keeps failing cannot loop.
    const sent = new Set<number>()
    while (!pauseRef.current && alive.current) {
      const before = runRef.current as Run
      const [next, ...rest] = before.chunks.filter(
        (part) =>
          !sent.has(part.index) && (part.status === 'pending' || resend.includes(part.status))
      )
      if (!next) break
      // Another tab may switch the session, and a part would then go to that session's database.
      if (activeSession() !== session) {
        toast.error(text('SESSION_CHANGED'))
        break
      }
      sent.add(next.index)
      // Shown as generating while PHP works on it, as the server records it meanwhile.
      setInFlight({index: next.index, sentAt: Date.now()})
      setQueued(new Set(rest.map((part) => part.index)))
      keep({
        ...before,
        chunks: before.chunks.map((part) =>
          part.index === next.index ? {...part, status: 'running'} : part
        ),
      })
      const reply = await ask((onSuccess, onFailure) =>
        runGenerateVoucherChunk(before.id, next.index, onSuccess, onFailure)
      )
      setInFlight(null)
      if (!reply.ok) {
        toast.error(reply.message)
        // Node may hold the part as running already: the page shows the run as node recorded it.
        const recorded = activeSession() === session ? await reread(before.id) : null
        keep(recorded ?? before)
        break
      }
      keep(reply.run)
      if (STOPS.includes(reply.chunk.status)) break
    }
    setQueued(null)
    setBusy(false)
  }

  const checkUndecided = async (start: Run) => {
    if (drivingRef.current) return
    keep(start)
    setBusy(true)
    for (const part of start.chunks) {
      if (part.status !== 'unknown' && part.status !== 'running') continue
      const reply = await ask((onSuccess, onFailure) =>
        checkGenerateVoucherChunk(start.id, part.index, onSuccess, onFailure)
      )
      if (!reply.ok) {
        toast.error(reply.message)
        break
      }
      keep(reply.run)
    }
    setBusy(false)
  }

  const update = (next: Run) => {
    if (runRef.current?.id === next.id) keep(next)
  }

  const forget = () => {
    if (!drivingRef.current) keep(null)
  }

  // Set again on every mount: development re-mounts run the cleanup first.
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  // The tab's title shows how far the run is, and that it finished until the user looks again.
  useEffect(() => {
    if (driving && run) {
      baseTitle.current ??= document.title
      document.title = `${runProgress(run).percent}% · ${text('NOTICE.GENERATING')}`
      return
    }
    if (baseTitle.current === null) return
    const restore = baseTitle.current
    baseTitle.current = null
    if (run?.state !== 'completed' || !document.hidden) {
      document.title = restore
      return
    }
    document.title = text('NOTICE.DONE')
    const back = () => {
      if (document.hidden) return
      document.title = restore
      document.removeEventListener('visibilitychange', back)
    }
    document.addEventListener('visibilitychange', back)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, driving])

  return (
    <RunDriverContext.Provider
      value={{
        run,
        driving,
        inFlight,
        queued,
        seen,
        drive,
        pause: () => (pauseRef.current = true),
        checkUndecided,
        update,
        forget,
        markSeen: setSeen,
      }}
    >
      {children}
      <RunNotice />
    </RunDriverContext.Provider>
  )
}

export default RunDriverProvider
